"use client"

import { useEffect } from "react"
import { Button } from "../../components/ui/button"
import { X } from "lucide-react"
import "./form-dialog.scss" 

export function FormDialog({
  trigger,
  title,
  subtitle,
  children,
  isOpen,
  onOpenChange,
  onSubmit,
  onCancel,
  submitLabel = "Create",
  cancelLabel = "Cancel",
  submitDisabled = false,
  hideCancel = false
}) {
  
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleCancel()
      }
    }
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    }
    if (onOpenChange) {
      onOpenChange(false)
    }
  }

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit()
    }
  }

  const handleOverlayClick = (e) => {
    // Only close if clicking the overlay itself, not the modal content
    if (e.target === e.currentTarget) {
      handleCancel()
    }
  }

  const handleTriggerClick = () => {
    if (onOpenChange) {
      onOpenChange(true)
    }
  }

  if (!isOpen) {
    return (
      <div onClick={handleTriggerClick}>
        {trigger}
      </div>
    )
  }

  return (
    <>
      <div onClick={handleTriggerClick} style={{ display: isOpen ? 'none' : 'block' }}>
        {trigger}
      </div>

      <div
        className="fixed inset-0 z-[1300] flex items-center justify-center"
        style={{ 
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          animation: isOpen ? 'fadeIn 0.2s ease-out' : 'fadeOut 0.2s ease-out'
        }}
        onClick={handleOverlayClick}
      >
        <div
          className="bg-white border-0 shadow-2xl rounded-[4px] relative w-full mx-4 max-w-[600px] flex flex-col"
          style={{
            maxHeight: 'calc(100vh - 64px)', // Ensure modal doesn't exceed viewport
            animation: isOpen ? 'slideIn 0.2s ease-out' : 'slideOut 0.2s ease-out'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white z-[1310] border-b border-gray-100 rounded-t-[4px]">
            <button
              onClick={handleCancel}
              className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 focus:outline-none z-[1320] transition-opacity"
              type="button"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </button>

            <div className="text-center py-5 px-6">
              <h2 className="text-2xl font-semibold text-center">
                {title} {subtitle && <span className="text-orange-500">{subtitle}</span>}
              </h2>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6" style={{ position: 'relative', zIndex: 1305 }}>
            {children}
          </div>

          <div className="sticky bottom-0 bg-white border-t border-gray-100 rounded-b-[4px] z-[1310]">
            <div className="flex gap-4 px-6 py-6">
              <Button 
                variant="outline" 
                onClick={handleCancel} 
                className={`flex-1 h-12 border-gray-300 hover:bg-gray-50 ${hideCancel ? 'invisible': ''}`}
                type="button"
              >
                {cancelLabel}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitDisabled}
                className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white disabled:bg-gray-300 disabled:text-gray-500"
                type="button"
              >
                {submitLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}